/**
 * SpaceShard.ts
 * Pooled Constellation Shard collectible for the World 2 space shmup.
 *
 * Stationary in world space — the camera passes the shard by at
 * {@link ../../constants/spaceConstants.SPACE_AUTO_SCROLL_SPEED}. Each
 * shard carries a stable string id so collection survives the round-trip
 * into {@link ../levels/spaceLevelSession.persistSpaceLevelCompletion}
 * and the existing
 * {@link ../data/worldProgress.countCollectedShards} +
 * {@link ../data/worldUnlock.isSecretStageUnlocked} gate.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module SpaceShard
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  SPACE_SHARD_WIDTH_PX,
  SPACE_SHARD_HEIGHT_PX,
  SPACE_SHARD_OFFSCREEN_MARGIN_PX,
} from '../../constants/spaceShardConstants';

/**
 * Pooled space-side Constellation Shard. Lives in the
 * `spaceShardPool` (see `spaceShardSystem.ts`); use `spawn()` after
 * `acquire()`.
 */
export class SpaceShard extends BaseEntity {
  private static nextSpawnId = 1;

  /** Re-issued on every spawn so the renderer gets a stable key per "use". */
  public spawnId = 0;

  /** Stable per-level id (e.g. `'2-1-shard-1'`). Empty string while pooled. */
  public shardId = '';

  /** True while this shard is in the active list. */
  public active = false;

  /**
   * True once the ship has collected this shard. Kept distinct from
   * `dead` so the collection step can mark the shard collected without
   * the cull pass also reading the corpse as "drifted off naturally".
   */
  public collected = false;

  constructor() {
    super(0, 0, SPACE_SHARD_WIDTH_PX, SPACE_SHARD_HEIGHT_PX);
    this.dead = true;
  }

  /**
   * Initialise the shard at a world position with a stable id.
   *
   * @param shardId - Stable shard id (must match the level config entry).
   * @param x  - Spawn X (world space, top-left of hitbox).
   * @param y  - Spawn Y (world space, top-left of hitbox).
   */
  public spawn(shardId: string, x: number, y: number): void {
    this.spawnId = SpaceShard.nextSpawnId++;
    this.shardId = shardId;
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.width = SPACE_SHARD_WIDTH_PX;
    this.height = SPACE_SHARD_HEIGHT_PX;
    this.dead = false;
    this.active = true;
    this.collected = false;
  }

  /** Pool-recycle hook. */
  public reset(): void {
    this.shardId = '';
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.width = SPACE_SHARD_WIDTH_PX;
    this.height = SPACE_SHARD_HEIGHT_PX;
    this.dead = true;
    this.active = false;
    this.collected = false;
  }

  /**
   * Advance one fixed timestep. Shards are stationary so the only work
   * here is the camera-relative cull — once a shard's right edge passes
   * the camera's left margin it is gone for the session.
   *
   * @param dt    - Fixed timestep in seconds (unused — shards do not move).
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void dt;
    void level;
    if (!this.active) return;
    const leftCullX = spaceState.cameraX - SPACE_SHARD_OFFSCREEN_MARGIN_PX;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
